import type { AircraftData } from '#entities';

type AircraftEvent = AircraftData['aircraftEvents'][number];

/**
 * Parses aircraft events and marks matching child events with trial flag.
 *
 * Searches through aircraft event children to identify those matching the
 * combination of UNOS ID and organ short name, marking them with `hasTrial: true`
 * when a match is found on a trial case.
 *
 * @param events - Array of aircraft events to process
 * @param unosId - UNOS identifier for matching`
 * @param organShort - Organ short name for matching
 * @param onTrialCase - Flag indicating if this is a trial case
 * @returns Processed events array with trial flags, or original events if conditions not met
 */
export const parseAircraftEvents = (
  events: AircraftEvent[],
  unosId: string | null,
  organShort: string | null,
  onTrialCase: boolean,
): AircraftEvent[] => {
  if (!(events.length && unosId && organShort && onTrialCase)) {
    return events;
  }

  const matchKey = `${unosId}${organShort}`.replace(/[\s,]/g, '').toUpperCase();
  let hasAnyChanges = false;

  const result = events.map(e => {
    if (!e?.children?.length) {
      return e;
    }

    let changed = false;

    const children = e.children.map(child => {
      const normalizedChild = (child.label ?? '')
        .replace(/[\s,]/g, '')
        .toUpperCase();

      if (normalizedChild === matchKey) {
        changed = true;
        hasAnyChanges = true;
        return { ...child, hasTrial: true };
      }

      return child;
    });

    return changed ? { ...e, children } : e;
  });

  return hasAnyChanges ? result : events;
};
